import { EmailHistory } from '../email/EmailHistory';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import RequestQuoteRounded from '@mui/icons-material/RequestQuoteRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import ShoppingCartCheckoutRounded from '@mui/icons-material/ShoppingCartCheckoutRounded';
import ThumbDownAltRounded from '@mui/icons-material/ThumbDownAltRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTabParam } from '../../lib/useTabParam';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Money, Qty } from '../../components/Money';
import { EmptyState } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { businessDate, dateTime, money } from '../../lib/format';
import { quotationDoc, WhatsappA4, WhatsappHistory } from '../whatsapp';
import { AttachmentsPanel } from '../attachments';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { todayIn, useLocations } from '../inventory/model';
import { DetailHeader, LocationSelect } from '../inventory/shared';
import { trimZeros } from './forms';
import {
  idem,
  quotationActions,
  quotationKeys,
  validityText,
  type Quotation,
  type QuotationLine,
} from './model';
import { MoreMenu, type MoreAction } from '../../components/MoreMenu';
import { useLinkedOne } from '../../components/useLinked';

type Decision = 'ACCEPTED' | 'REJECTED';

export function QuotationDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const { format, settings } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const today = todayIn(settings?.timeZone ?? 'Asia/Karachi');
  const [tab, setTab] = useTabParam(['items', 'notes', 'activity'] as const);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [converting, setConverting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: q, error } = useQuery({
    queryKey: quotationKeys.detail(id),
    queryFn: () => unwrap(api.GET('/quotations/{id}', { params: { path: { id } } })),
  });
  // The quotation does not link its order: find it among the customer's orders.
  const order = useQuery({
    queryKey: quotationKeys.order(id),
    queryFn: async () => {
      const orders = await unwrap(
        api.GET('/sales-orders', {
          params: { query: { contactId: q?.contact.id, pageSize: 100 } },
        }),
      );
      return orders.data.find((o) => o.quotationId === id) ?? null;
    },
    enabled: q?.status === 'CONVERTED' && can('sales.view'),
  });
  const linkedOrder = useLinkedOne('order', order.data?.id);

  const changeStatus = async (status: 'DRAFT' | 'SENT' | Decision, reason?: string) => {
    if (!q) return;
    setBusy(true);
    try {
      const updated = await unwrap(
        api.POST('/quotations/{id}/status', {
          params: { path: { id } },
          body: { status, version: q.version, ...(reason ? { reason } : {}) },
        }),
      );
      queryClient.setQueryData(quotationKeys.detail(id), updated);
      await queryClient.invalidateQueries({ queryKey: quotationKeys.all });
      notify.success(
        {
          DRAFT: `${updated.number} moved back to draft`,
          SENT: `${updated.number} marked as sent`,
          ACCEPTED: `${updated.number} accepted`,
          REJECTED: `${updated.number} rejected`,
        }[status],
      );
    } catch (e) {
      notify.error(e);
    } finally {
      setBusy(false);
    }
  };

  if (error)
    return (
      <EmptyState
        icon={<RequestQuoteRounded />}
        title="Quotation not found"
        body="It may have been deleted or the link is wrong."
        action={{ label: 'Back to quotations', onClick: () => void navigate('/quotations') }}
      />
    );

  const actions = q ? quotationActions(q.status, can) : null;
  const primary: ReactNode =
    !q || !actions ? null : actions.convert && q.status === 'ACCEPTED' ? (
      <Button
        variant="contained"
        startIcon={<ShoppingCartCheckoutRounded />}
        onClick={() => setConverting(true)}
      >
        Convert to order
      </Button>
    ) : actions.send ? (
      <Button
        variant="contained"
        startIcon={<SendRounded />}
        loading={busy}
        onClick={() => void changeStatus('SENT')}
      >
        Mark as sent
      </Button>
    ) : actions.accept ? (
      <Button
        variant="contained"
        startIcon={<CheckCircleRounded />}
        onClick={() => setDecision('ACCEPTED')}
      >
        Customer accepted
      </Button>
    ) : actions.edit ? (
      <Button
        variant="contained"
        startIcon={<EditRounded />}
        onClick={() => void navigate(`/quotations/${id}/edit`)}
      >
        Edit & extend
      </Button>
    ) : q.status === 'CONVERTED' && order.data ? (
      <Button
        variant="contained"
        startIcon={<ReceiptLongRounded />}
        onClick={() => void navigate(`/sales/orders/${order.data?.id ?? ''}`)}
      >
        Open {order.data.number}
      </Button>
    ) : null;

  const menuItems: MoreAction[] =
    !q || !actions
      ? []
      : ([
          actions.edit && q.status !== 'EXPIRED'
            ? {
                key: 'edit',
                label: 'Edit',
                icon: <EditRounded />,
                onClick: () => void navigate(`/quotations/${id}/edit`),
              }
            : null,
          actions.accept && q.status === 'DRAFT'
            ? {
                key: 'accept',
                label: 'Customer accepted',
                icon: <CheckCircleRounded />,
                onClick: () => setDecision('ACCEPTED'),
              }
            : null,
          actions.convert && q.status !== 'ACCEPTED'
            ? {
                key: 'convert',
                label: 'Convert to order',
                icon: <ShoppingCartCheckoutRounded />,
                onClick: () => setConverting(true),
              }
            : null,
          actions.reopen
            ? {
                key: 'reopen',
                label: 'Back to draft',
                icon: <UndoRounded />,
                onClick: () => void changeStatus('DRAFT'),
              }
            : null,
          actions.reject
            ? {
                key: 'reject',
                label: 'Customer rejected',
                icon: <ThumbDownAltRounded />,
                onClick: () => setDecision('REJECTED'),
              }
            : null,
          actions.remove
            ? {
                key: 'delete',
                label: 'Delete',
                icon: <DeleteOutlineRounded />,
                onClick: () => setDeleting(true),
                danger: true,
              }
            : null,
        ].filter(Boolean) as MoreAction[]);

  const open = q?.status === 'DRAFT' || q?.status === 'SENT';

  return (
    <>
      <DetailHeader
        back="/quotations"
        title={q?.number ?? 'Quotation'}
        chips={q && <StatusChip status={q.status} />}
        subtitle={q ? `${q.contact.name} · ${businessDate(q.docDate, format)}` : undefined}
        actions={
          <>
            {q && (
              <>
                <WhatsappA4
                  type="quotation"
                  id={q.id}
                  number={q.number}
                  contact={q.contact}
                  vars={{
                    amount: money(q.total, format),
                    date: businessDate(q.docDate, format),
                  }}
                  doc={() => quotationDoc(q, format)}
                />
                <MoreMenu
                  linked={[linkedOrder]}
                  onDuplicate={
                    can('quotations.create')
                      ? () => void navigate('/quotations/new', { state: { copy: q } })
                      : undefined
                  }
                  exportModel="quotation"
                  exportId={q.id}
                  actions={menuItems}
                />
                {primary}
              </>
            )}
          </>
        }
      />

      {q?.status === 'EXPIRED' && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          This quotation expired on {businessDate(q.validUntil, format)}. Edit it to extend the
          validity before sending or converting it.
        </Alert>
      )}
      {q?.status === 'REJECTED' && q.decisionReason && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2.5 }}>
          Rejected: {q.decisionReason}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          mb: 2.5,
        }}
      >
        <Fact label="Customer" value={q?.contact.name} hint={q?.contact.phone ?? undefined} wide />
        <Fact
          label="Valid until"
          value={q ? (q.validUntil ? businessDate(q.validUntil, format) : 'No expiry') : undefined}
          hint={
            q && (open || q.status === 'EXPIRED') ? validityText(q.validUntil, today) : undefined
          }
          hintTone={q?.status === 'EXPIRED' ? 'error.main' : undefined}
        />
        <Fact
          label="Total"
          value={q ? <Money value={q.total} strong /> : undefined}
          hint={q ? `${String(q.lines.length)} item${q.lines.length === 1 ? '' : 's'}` : undefined}
          accent
        />
      </Box>

      <Card>
        <Tabs
          value={tab}
          onChange={(_, v: typeof tab) => setTab(v)}
          sx={{
            px: 2,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { fontWeight: 600 },
          }}
        >
          <Tab value="items" label="Items" />
          <Tab value="notes" label="Notes & terms" />
          <Tab value="activity" label="Activity" />
        </Tabs>
        {tab === 'items' && <ItemsTab q={q} />}
        {tab === 'notes' && q && (
          <CardContent>
            <Stack spacing={3}>
              <TextBlock label="Reference" value={q.reference} />
              <TextBlock label="Notes" value={q.notes} />
              <TextBlock label="Terms & conditions" value={q.terms} />
            </Stack>
          </CardContent>
        )}
        {tab === 'activity' && q && (
          <CardContent>
            <Timeline
              events={[
                { label: 'Created', at: q.createdAt },
                q.sentAt ? { label: 'Sent to the customer', at: q.sentAt } : null,
                q.decidedAt
                  ? {
                      label: q.status === 'REJECTED' ? 'Rejected' : 'Accepted',
                      at: q.decidedAt,
                      detail: q.decisionReason ?? undefined,
                    }
                  : null,
                q.status === 'CONVERTED'
                  ? {
                      label: `Converted to ${order.data?.number ?? 'a sales order'}`,
                      at: order.data?.createdAt ?? q.updatedAt,
                    }
                  : null,
                { label: 'Last updated', at: q.updatedAt },
              ]}
            />
          </CardContent>
        )}
      </Card>

      {q && decision && (
        <DecisionDialog
          decision={decision}
          number={q.number}
          onClose={() => setDecision(null)}
          onSubmit={async (reason) => {
            await changeStatus(decision, reason);
            setDecision(null);
          }}
        />
      )}
      {q && converting && (
        <ConvertDialog quotation={q} today={today} onClose={() => setConverting(false)} />
      )}
      <ConfirmDialog
        open={deleting}
        title={`Delete ${q?.number ?? 'quotation'}?`}
        body="The quotation is removed from the list. Its number is not reused. This can't be undone."
        confirmLabel="Delete"
        danger
        onClose={() => setDeleting(false)}
        onConfirm={async () => {
          if (!q) return;
          try {
            await unwrap(
              api.DELETE('/quotations/{id}', {
                params: { path: { id }, query: { version: q.version } },
              }),
            );
            await queryClient.invalidateQueries({ queryKey: quotationKeys.all });
            notify.success(`${q.number} deleted`);
            void navigate('/quotations', { replace: true });
          } catch (e) {
            notify.error(e);
          }
        }}
      />
      {q && <AttachmentsPanel recordType="QUOTATION" recordId={q.id} sx={{ mt: 3 }} />}
      {q && <WhatsappHistory documentType="quotation" documentId={q.id} sx={{ mt: 3 }} />}
      {q && <EmailHistory documentType="quotation" documentId={q.id} sx={{ mt: 3 }} />}
    </>
  );
}

function ItemsTab({ q }: { q: Quotation | undefined }) {
  const { format } = useShop();
  const hasDiscount =
    q?.lines.some((l) => Number(l.discountAmount) + Number(l.documentDiscount) > 0) ?? false;
  const hasTax = q?.lines.some((l) => Number(l.taxAmount) > 0) ?? false;
  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={44} sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                #
              </TableCell>
              <TableCell>Item</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                Price
              </TableCell>
              {hasDiscount && (
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  Discount
                </TableCell>
              )}
              {hasTax && (
                <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  Tax
                </TableCell>
              )}
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!q &&
              [0, 1, 2].map((i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ))}
            {q?.lines.map((l) => (
              <LineRow key={l.id} l={l} hasDiscount={hasDiscount} hasTax={hasTax} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {q && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            p: 2.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Stack spacing={1} sx={{ width: { xs: '100%', sm: 340 } }}>
            <Row label="Subtotal" value={money(q.subtotal, format)} />
            {Number(q.discountTotal) > 0 && (
              <Row
                label={
                  q.discount
                    ? `Discounts (incl. ${q.discount.type === 'PERCENT' ? `${trimZeros(q.discount.value)}%` : money(q.discount.value, format)} on total)`
                    : 'Discounts'
                }
                value={`−${money(q.discountTotal, format)}`}
                tone="success.main"
              />
            )}
            {Number(q.taxTotal) > 0 && (
              <Row
                label={q.pricesIncludeTax ? 'Tax included' : 'Tax'}
                value={money(q.taxTotal, format)}
              />
            )}
            <Divider />
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography variant="h6">Total</Typography>
              <Typography variant="h4" className="tabular" sx={{ fontWeight: 750 }}>
                {money(q.total, format)}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      )}
    </>
  );
}

function LineRow({
  l,
  hasDiscount,
  hasTax,
}: {
  l: QuotationLine;
  hasDiscount: boolean;
  hasTax: boolean;
}) {
  const { format } = useShop();
  const off = Number(l.discountAmount) + Number(l.documentDiscount);
  return (
    <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
      <TableCell
        className="tabular"
        sx={{ color: 'text.disabled', display: { xs: 'none', sm: 'table-cell' } }}
      >
        {l.lineNo}
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {l.description}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {l.sku}
          {l.factor !== '1' && Number(l.factor) !== 1
            ? ` · ${trimZeros(l.factor)} per ${l.unitSymbol}`
            : ''}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Qty value={l.qty} unit={l.unitSymbol} />
      </TableCell>
      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        <Money value={l.unitPrice} />
      </TableCell>
      {hasDiscount && (
        <TableCell
          align="right"
          sx={{
            display: { xs: 'none', md: 'table-cell' },
            color: off ? 'success.main' : 'text.disabled',
          }}
        >
          {off ? `−${money(off, format)}` : '—'}
        </TableCell>
      )}
      {hasTax && (
        <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
          {Number(l.taxAmount) ? (
            <>
              <Money value={l.taxAmount} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {trimZeros(l.taxRate)}%
              </Typography>
            </>
          ) : (
            '—'
          )}
        </TableCell>
      )}
      <TableCell align="right">
        <Money value={l.lineTotal} strong />
      </TableCell>
    </TableRow>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" className="tabular" sx={{ fontWeight: 600, color: tone }}>
        {value}
      </Typography>
    </Stack>
  );
}

function Fact({
  label,
  value,
  hint,
  hintTone,
  wide,
  accent,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  hintTone?: string;
  wide?: boolean;
  accent?: boolean;
}) {
  return (
    <Card
      sx={[
        { gridColumn: wide ? { xs: '1 / -1', md: 'span 2' } : undefined },
        accent
          ? (t) => {
              const p = (t.vars ?? t).palette.primary.main;
              return {
                background: `linear-gradient(150deg, color-mix(in srgb, ${p} 9%, transparent), transparent 70%)`,
              };
            }
          : {},
      ]}
    >
      <CardContent sx={{ py: '16px !important' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {value === undefined ? (
          <Skeleton width="60%" height={30} />
        ) : (
          <Typography
            component="div"
            sx={{
              fontWeight: 650,
              fontSize: accent ? { xs: 18, sm: 22 } : 17,
              mt: 0.25,
              overflowWrap: 'anywhere',
            }}
          >
            {value}
          </Typography>
        )}
        {hint !== undefined && (
          <Typography variant="caption" sx={{ color: hintTone ?? 'text.secondary' }}>
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function TextBlock({ label, value }: { label: string; value: string | null }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ whiteSpace: 'pre-wrap', color: value ? 'text.primary' : 'text.disabled' }}>
        {value ?? 'None'}
      </Typography>
    </Box>
  );
}

function Timeline({
  events,
}: {
  events: ({ label: string; at: string; detail?: string } | null)[];
}) {
  const { format } = useShop();
  const list = events.filter(
    (e): e is { label: string; at: string; detail?: string } => e !== null,
  );
  return (
    <Stack spacing={0}>
      {list.map((e, i) => (
        <Stack key={`${e.label}-${String(i)}`} direction="row" spacing={2}>
          <Stack sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                mt: 0.6,
                borderRadius: '50%',
                bgcolor: i === 0 ? 'primary.main' : 'background.paper',
                border: 2,
                borderColor: 'primary.main',
              }}
            />
            {i < list.length - 1 && <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', my: 0.5 }} />}
          </Stack>
          <Box sx={{ pb: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 650 }}>
              {e.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dateTime(e.at, format)}
            </Typography>
            {e.detail && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                “{e.detail}”
              </Typography>
            )}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

function DecisionDialog({
  decision,
  number,
  onClose,
  onSubmit,
}: {
  decision: Decision;
  number: string;
  onClose: () => void;
  onSubmit: (reason: string | undefined) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const accepted = decision === 'ACCEPTED';
  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {accepted ? `Mark ${number} as accepted?` : `Mark ${number} as rejected?`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography color="text.secondary">
            {accepted
              ? 'The quotation is locked and ready to convert into a sales order.'
              : 'The quotation is closed. You can move it back to draft later if the customer changes their mind.'}
          </Typography>
          <TextField
            label={accepted ? 'Note (optional)' : 'Reason (optional)'}
            placeholder={accepted ? 'e.g. Confirmed by phone' : 'e.g. Price too high'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 500 } }}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={accepted ? 'success' : 'error'}
          loading={busy}
          onClick={() => {
            setBusy(true);
            void onSubmit(reason.trim() || undefined).finally(() => setBusy(false));
          }}
        >
          {accepted ? 'Mark accepted' : 'Mark rejected'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConvertDialog({
  quotation,
  today,
  onClose,
}: {
  quotation: Quotation;
  today: string;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const locations = useLocations();
  const [locationId, setLocationId] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const location = locationId || locations.data?.find((l) => l.isDefault && l.isSellable)?.id || '';

  const convert = async () => {
    setBusy(true);
    try {
      const order = await unwrap(
        api.POST('/quotations/{id}/convert', {
          params: { path: { id: quotation.id }, header: idem() },
          body: {
            version: quotation.version,
            ...(location ? { locationId: location } : {}),
            expectedDate: expectedDate || null,
            confirm,
          },
        }),
      );
      queryClient.setQueryData(quotationKeys.order(quotation.id), order);
      await queryClient.invalidateQueries({ queryKey: quotationKeys.all });
      notify.success(`${quotation.number} converted to ${order.number}`);
      onClose();
      void navigate(`/sales/orders/${order.id}`);
    } catch (e) {
      notify.error(e);
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Convert {quotation.number} to a sales order
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          <Typography color="text.secondary">
            The order copies every line at the quoted prices ({money(quotation.total, format)} for{' '}
            {quotation.contact.name}). A quotation converts only once.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <LocationSelect value={location} onChange={setLocationId} label="Deliver from" />
            <TextField
              type="date"
              label="Expected delivery"
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
              helperText="Optional"
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: today } }}
              sx={{ flex: 1 }}
            />
          </Stack>
          {can('sales.confirm') && (
            <FormControlLabel
              control={
                <Checkbox checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Confirm the order now
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reserves the stock straight away. Leave unticked to keep the order as a draft.
                  </Typography>
                </Box>
              }
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<ShoppingCartCheckoutRounded />}
          loading={busy}
          onClick={() => void convert()}
        >
          Create sales order
        </Button>
      </DialogActions>
    </Dialog>
  );
}
